import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { getBalancesOnClient, exportClientBalanceHistory } from "@/Api/clients";
import { toast } from "sonner";
import { Mask } from "@/utils/mask";
import { Eye, ExternalLink, FileText, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download, Loader } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface BalanceModalProps {
  open: boolean;
  onClose: () => void;
  clientId?: string;
  clientName: string;
  currentBalance?: number;
}

interface BalanceFile {
  id: number;
  uuid: string;
  file: string;
  description?: string;
  created_by: number;
}

interface ContestInvestigationSale {
  id: number;
  uuid: string;
  sale_id: number | null;
  contest_product_id: number;
  product_classification_id: number;
  investigation_price_per_share: number;
  investigation_price_total: number;
  receive_by_cashier: boolean;
  non_winning_ticket: number;
  product_classification?: {
    id: number;
    uuid: string;
    name: string;
    lottery?: {
      id: number;
      name: string;
      color: string;
    };
  };
  contest_product_classification?: {
    id: number;
    product_name: string;
    contest_id: number;
    contest?: {
      id: number;
      name: string;
    };
    lottery?: {
      id: number;
      name: string;
      color: string;
    };
    product_classification?: {
      id: number;
      name: string;
    };
  };
}

interface BalanceTransaction {
  id: number;
  uuid: string;
  client_id: number;
  contest_investigation_id: number | null;
  balance: number;
  balance_flow_type_id: number;
  balance_origin_type_id: number;
  file?: string;
  files?: BalanceFile[];
  description?: string;
  is_receive_by_cashier?: boolean;
  prize_value?: number;
  client_quotas?: number;
  created_by: {
    id: number;
    name: string;
    email: string;
  };
  created_at: string;
  updated_at: string;
  balance_flow_type: {
    id: number;
    name: string;
  };
  balance_origin_type: {
    id: number;
    name: string;
  };
  client: {
    id: number;
    name: string;
    email: string;
    phone: string;
    addresses: Array<{
      id: number;
      street: string;
      number: string;
      complement: string | null;
      neighborhood: string;
      city: string;
      state: string;
      zip_code: string;
      latitude: string | null;
      longitude: string | null;
      pivot: {
        client_id: number;
        address_id: number;
        is_main: number;
        reference: string | null;
        created_at: string;
        updated_at: string;
      };
    }>;
  };
  contest_investigation_sale: ContestInvestigationSale | null;
}

export const BalanceModal = ({
  open,
  onClose,
  clientId,
  clientName,
  currentBalance = 0,
}: BalanceModalProps) => {
  const [transactions, setTransactions] = useState<BalanceTransaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);

  // Estados do modal de detalhes da transação
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<BalanceTransaction | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (open && clientId) {
      setCurrentPage(1);
      fetchTransactions(1);
    }
  }, [open, clientId]);

  const fetchTransactions = async (page: number = 1) => {
    try {
      setLoadingTransactions(true);
      const { data } = await getBalancesOnClient(clientId || "", page);
      if (data?.response) {
        setTransactions(data.response.data || []);
        setCurrentPage(data.response.current_page || 1);
        setLastPage(data.response.last_page || 1);
        setTotalTransactions(data.response.total || 0);
      }
    } catch (error) {
      console.error("Erro ao buscar transações:", error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const isImageFile = (url: string) => {
    return /\.(jpg|jpeg|png|gif|webp)/i.test(url);
  };

  const getFileNameFromUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const fileName = pathname.split("/").pop() || "arquivo";
      return decodeURIComponent(fileName);
    } catch {
      const parts = url.split("/");
      return parts.pop() || "arquivo";
    }
  };

  const handleViewDetails = (transaction: BalanceTransaction) => {
    setSelectedTransaction(transaction);
    setDetailsModalOpen(true);
  };

  const handleExportHistory = async () => {
    if (!clientId) {
      toast.error("ID do cliente não encontrado");
      return;
    }

    setIsExporting(true);
    try {
      const blob = await exportClientBalanceHistory(clientId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = clientName.replace(/\s+/g, "-").toLowerCase();
      a.download = `historico-transacoes-${safeName}-${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Histórico exportado com sucesso!");
    } catch (error) {
      console.error("Erro ao exportar histórico:", error);
      toast.error("Erro ao exportar histórico de transações");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Saldo do Cliente</DialogTitle>
          <DialogDescription>
            Cliente: <span className="font-medium">{clientName}</span>
            {currentBalance !== undefined && (
              <>
                {" "}· Saldo atual:{" "}
                <span className="font-medium">{Mask.formatCurrency(currentBalance)}</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold">
              Histórico de Transações
            </h3>
            {transactions.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportHistory}
                disabled={isExporting}
                className="gap-2"
              >
                {isExporting ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Exportar
              </Button>
            )}
          </div>
          {loadingTransactions ? (
            <p className="text-center py-4 text-gray-500">
              Carregando transações...
            </p>
          ) : transactions.length > 0 ? (
            <ScrollArea className="h-[200px] rounded-md border p-2">
              <div className="space-y-3">
                {transactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="border-b pb-2 last:border-0"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex justify-between items-center">
                          <span className="font-medium">
                            {transaction.balance_origin_type.name}
                          </span>

                          {(transaction.file || (transaction.files && transaction.files.length > 0) || transaction.description || (transaction.balance_origin_type_id === 1 && transaction.contest_investigation_sale)) && (
                            <Button
                              size="icon"
                              className="h-8 w-8 ml-2"
                              onClick={() => handleViewDetails(transaction)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <span
                            className={
                              transaction.is_receive_by_cashier
                                ? "text-blue-600 font-semibold"
                                : transaction.balance_flow_type_id === 1
                                ? "text-green-600"
                                : "text-red-600"
                            }
                          >
                            {transaction.balance_flow_type_id === 1 ? "+ " : ""}
                            {Mask.formatCurrency(
                              transaction.is_receive_by_cashier
                                ? transaction.prize_value || 0
                                : transaction.balance
                            )}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 flex justify-between mt-1">
                          <span>{formatDate(transaction.created_at)}</span>
                          <span>{transaction?.balance_flow_type?.name}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          <span>
                            Criado por: {transaction?.created_by?.name}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <p className="text-center py-4 text-gray-500">
              Nenhuma transação encontrada
            </p>
          )}

          {/* Controles de Paginação */}
          {lastPage > 1 && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t">
              <span className="text-xs text-gray-500">
                {totalTransactions} transação(ões)
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchTransactions(1)}
                  disabled={currentPage <= 1 || loadingTransactions}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchTransactions(currentPage - 1)}
                  disabled={currentPage <= 1 || loadingTransactions}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm px-2">
                  {currentPage} / {lastPage}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchTransactions(currentPage + 1)}
                  disabled={currentPage >= lastPage || loadingTransactions}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchTransactions(lastPage)}
                  disabled={currentPage >= lastPage || loadingTransactions}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>

      {/* Modal de Detalhes da Transação */}
      <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="space-y-4">
            <h3 className="text-xl font-bold">Detalhes da Transação</h3>

            {selectedTransaction && (
              <>
                {/* Informações da Transação */}
                <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="text-sm text-gray-500">Tipo:</span>
                      <p className="font-medium">
                        {selectedTransaction.balance_origin_type.name}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Valor:</span>
                      <p
                        className={`font-bold ${
                          selectedTransaction.is_receive_by_cashier
                            ? "text-blue-600"
                            : selectedTransaction.balance_flow_type_id === 1
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {selectedTransaction.balance_flow_type_id === 1
                          ? "+ "
                          : ""}
                        {Mask.formatCurrency(
                          selectedTransaction.is_receive_by_cashier
                            ? selectedTransaction.prize_value || 0
                            : selectedTransaction.balance
                        )}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Data:</span>
                      <p className="font-medium">
                        {formatDate(selectedTransaction.created_at)}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Criado por:</span>
                      <p className="font-medium">
                        {selectedTransaction.created_by.name}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Descrição */}
                {selectedTransaction.description && (
                  <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                    <h4 className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      Descrição
                    </h4>
                    <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                      {selectedTransaction.description}
                    </p>
                  </div>
                )}

                {/* Detalhes da Premiação - quando é uma premiação com dados de investigação */}
                {selectedTransaction.balance_origin_type_id === 1 && selectedTransaction.contest_investigation_sale && (
                  <div className="border rounded-lg overflow-hidden">
                    <div
                      className="px-4 py-2 text-white font-semibold"
                      style={{
                        backgroundColor:
                          selectedTransaction.contest_investigation_sale.contest_product_classification?.lottery?.color ||
                          selectedTransaction.contest_investigation_sale.product_classification?.lottery?.color ||
                          "#023859"
                      }}
                    >
                      Detalhes da Premiação
                    </div>
                    <div className="p-4 bg-gray-50 dark:bg-gray-800 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <span className="text-sm text-gray-500">Loteria:</span>
                          <p className="font-medium">
                            {selectedTransaction.contest_investigation_sale.contest_product_classification?.lottery?.name ||
                              selectedTransaction.contest_investigation_sale.product_classification?.lottery?.name ||
                              "-"}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Concurso:</span>
                          <p className="font-medium">
                            {selectedTransaction.contest_investigation_sale.contest_product_classification?.contest?.name || "-"}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Produto:</span>
                          <p className="font-medium">
                            {selectedTransaction.contest_investigation_sale.contest_product_classification?.product_name || "-"} -{" "}
                            {selectedTransaction.contest_investigation_sale.product_classification?.name || "-"}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Cotas do Cliente:</span>
                          <p className="font-medium">{selectedTransaction.client_quotas || "-"}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Valor Total Apurado:</span>
                          <p className="font-medium text-green-600">
                            {Mask.formatCurrency(selectedTransaction.contest_investigation_sale.investigation_price_total || 0)}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Valor por Cota:</span>
                          <p className="font-medium">
                            {Mask.formatCurrency(selectedTransaction.contest_investigation_sale.investigation_price_per_share || 0)}
                          </p>
                        </div>
                      </div>
                      <div className="pt-2 border-t">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Valor da Premiação do Cliente:
                          </span>
                          <span className="text-lg font-bold text-blue-600">
                            {Mask.formatCurrency(selectedTransaction.prize_value || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Arquivos (novo formato) */}
                {selectedTransaction.files && selectedTransaction.files.length > 0 && (
                  <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      Arquivos Anexados ({selectedTransaction.files.length})
                    </h4>
                    <div className="space-y-3">
                      {selectedTransaction.files.map((balanceFile, index) => (
                        <div key={balanceFile.id || index} className="border rounded-md p-3 bg-white dark:bg-gray-900">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs text-gray-500">Arquivo {index + 1}</span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(balanceFile.file, "_blank")}
                              className="gap-2"
                            >
                              <ExternalLink className="h-4 w-4" />
                              Abrir
                            </Button>
                          </div>
                          {isImageFile(balanceFile.file) ? (
                            <img
                              src={balanceFile.file}
                              alt={`Comprovante ${index + 1}`}
                              className="w-full h-auto rounded-md border max-h-48 object-contain"
                              onError={(e) => {
                                e.currentTarget.src = "";
                                e.currentTarget.alt = "Erro ao carregar imagem";
                              }}
                            />
                          ) : (
                            <div className="flex items-center gap-2 p-2 bg-gray-100 dark:bg-gray-800 rounded">
                              <FileText className="h-5 w-5 text-red-500" />
                              <span className="text-sm truncate" title={getFileNameFromUrl(balanceFile.file)}>
                                {getFileNameFromUrl(balanceFile.file)}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Arquivo (formato antigo - compatibilidade) */}
                {selectedTransaction.file && (!selectedTransaction.files || selectedTransaction.files.length === 0) && (
                  <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Arquivo Anexado
                      </h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          window.open(selectedTransaction.file, "_blank")
                        }
                        className="gap-2"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Abrir em nova aba
                      </Button>
                    </div>

                    {isImageFile(selectedTransaction.file) ? (
                      <div className="mt-2">
                        <img
                          src={selectedTransaction.file}
                          alt="Comprovante"
                          className="w-full h-auto rounded-md border"
                          onError={(e) => {
                            e.currentTarget.src = "";
                            e.currentTarget.alt = "Erro ao carregar imagem";
                          }}
                        />
                      </div>
                    ) : (
                      <div className="mt-2 flex items-center justify-center p-8 border-2 border-dashed rounded-md">
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <FileText className="h-5 w-5 text-red-500" />
                            <p className="text-sm text-gray-500" title={getFileNameFromUrl(selectedTransaction.file)}>
                              {getFileNameFromUrl(selectedTransaction.file)}
                            </p>
                          </div>
                          <Button
                            variant="default"
                            onClick={() =>
                              window.open(selectedTransaction.file, "_blank")
                            }
                            className="gap-2"
                          >
                            <ExternalLink className="h-4 w-4" />
                            Visualizar PDF
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
